import { takeLatest, call, put, select } from 'redux-saga/effects';
import uniqBy from 'lodash/uniqBy';
import forEach from 'lodash/forEach';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { SET_SERVICE_ERROR } from 'yoda-site-components/lib/common/Constants';
import {
    ANALYTICS_PAGE_NAME_CART_GIFTCARD_SUCCESS,
    INVALID_GIFTCARD_ERROR_MSG,
    INVALID_GIFTCARD_CAPTCHA_MSG,
    GENERIC_ERROR_MSG,
} from 'yoda-checkout-components/lib/common/Constants';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import { getClientId } from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import { getGiftCardBalance } from 'yoda-interfaces/lib/GiftCard/GiftCardApi';
import { pageLoadSuccessAnalyticsAction } from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';

import {
    getAccertifyDeviceInfo,
    logExtendedAnalytics,
} from 'yoda-site-components/lib/sagas/SiteProbingSaga';
import { GET_GIFT_CARD_BALANCE } from '../actionTypes/CartActionTypes';
import { updateGiftCard, setGiftCardError } from '../actions/GiftCardActions';

function* getGiftCardBalanceSaga(actionParam) {
    const action = actionParam;
    const context = (state) => state.context;
    const appContext = yield select(context);
    const {
        deviceType,
        isNativeIos = false,
        isNativeAndroid = false,
        featureFlags: { enableADIGCBalance = true },
    } = appContext;
    action.payload.clientId = getClientId(deviceType, isNativeIos, isNativeAndroid);
    if (enableADIGCBalance) {
        const deviceInfo = yield call(getAccertifyDeviceInfo, {
            actionName: 'get_gc_balance',
            enableFeatureSpecificFlag: enableADIGCBalance,
        });
        if (deviceInfo) {
            action.payload.deviceInfo = deviceInfo;
        }
    }
    const result = yield call(FactorySaga, getGiftCardBalance, action);
    const { response } = result;
    if (result.isSuccess && response.data) {
        const payload = {
            giftCardNum: '',
            giftCardPin: '',
        };

        // Get current giftcard balances
        const gcState = (state) => state.giftCard;
        const giftcardBalances = yield select(gcState);
        const currBalances =
            giftcardBalances && giftcardBalances.giftCardBalances
                ? giftcardBalances.giftCardBalances
                : [];
        // Get max display card number in context preference
        payload.clientId = getClientId(deviceType, isNativeIos, isNativeAndroid);
        const maxGiftcards =
            appContext && appContext.preferences.maxGiftcardNumber
                ? appContext.preferences.maxGiftcardNumber
                : 2;
        // Add new card
        currBalances.unshift({
            cardNumber: response.data.cardNumber,
            balance: response.data.cardBalance,
        });
        // remove duplicate and extra
        payload.giftCardBalances = uniqBy(currBalances, 'cardNumber').slice(0, maxGiftcards);
        yield put(updateGiftCard(payload));
        // DA trigger giftcard success event
        yield put(pageLoadSuccessAnalyticsAction(ANALYTICS_PAGE_NAME_CART_GIFTCARD_SUCCESS));
    } else if (!result.isSuccess && Array.isArray(response.data)) {
        // Filter error
        const errors = {};
        const errorHandlerInfo = {};
        forEach(response.data, (e) => {
            switch (e.errorCode) {
                case 'API_ERR_GIFT_CARD_NUMBER_NOT_VALID':
                case 'API_ERR_GIFT_CARD_NUMBER_LENGTH_NOT_VALID':
                    errors.giftCardNumError = e.errorMessage;
                    break;
                case 'API_ERR_GIFT_CARD_PIN_NOT_VALID':
                case 'API_ERR_GIFT_CARD_PIN_LENGTH_NOT_VALID':
                    errors.giftCardPinError = e.errorMessage;
                    break;
                case 'API_ERR_INVALID_GIFTCARD':
                    errorHandlerInfo.errorCode = e.errorCode;
                    // Hanle error 33: Aurora return different msg for same error code
                    errorHandlerInfo.errorMessage =
                        e.errorCode === e.errorMessage
                            ? INVALID_GIFTCARD_ERROR_MSG
                            : e.errorMessage;
                    errorHandlerInfo.context = 'GIFTCARDBALANCE';
                    errorHandlerInfo.errorComponent = 'GIFT_BALANCE';
                    break;
                case 'INVALID_CAPTCHA_INPUT':
                    errorHandlerInfo.errorCode = e.errorCode;
                    errorHandlerInfo.errorMessage = INVALID_GIFTCARD_CAPTCHA_MSG;
                    errorHandlerInfo.context = 'GIFTCARDBALANCE';
                    errorHandlerInfo.errorComponent = 'GIFT_BALANCE';
                    break;
                default:
                    errorHandlerInfo.errorCode = e.errorCode;
                    errorHandlerInfo.errorMessage = e.errorMessage;
                    errorHandlerInfo.context = 'GIFTCARDBALANCE';
                    errorHandlerInfo.errorComponent = 'GIFT_BALANCE';
            }
        });

        yield put(setGiftCardError(errors));
        yield put({ type: SET_SERVICE_ERROR, errorHandlerInfo });
    } else {
        const errorHandlerInfo = {
            errorCode: 'GIFTCARD_ERROR',
            errorMessage: GENERIC_ERROR_MSG,
            context: 'GIFTCARDBALANCE',
        };
        yield put({ type: SET_SERVICE_ERROR, errorHandlerInfo });
    }
    if (enableADIGCBalance) {
        const adiStatus = result?.response?.headers?.get('x-adi-sts');
        // eslint-disable-next-line no-unused-expressions
        ['A', 'R'].includes(adiStatus)
            ? yield call(logExtendedAnalytics, {
                  result,
                  isTriggerFromLS: false,
                  actionMethod: 'GET_GIFT_CARD_BALANCE',
              })
            : '';
    }
    // Stop global loader
    yield put(showGlobalLoader(false));
}

const watchGiftCard = function* watchGiftCard() {
    yield takeLatest(GET_GIFT_CARD_BALANCE, getGiftCardBalanceSaga);
};
watchGiftCard.sagaName = 'watchGiftCard';

export default watchGiftCard;
